/**
 * Journey Web - Main Application Component
 *
 * The Journey Web portal is designed for TALENTS going through
 * their mobility journey. Desktop/tablet-only experience.
 *
 * Per Tonnie Platform TECH Guidelines:
 * - Minimum viewport: 768px (md breakpoint)
 * - Mobile users (<768px) are redirected to Citizen Mobile app
 *
 * Root providers (SdkProvider, Clerk AuthProvider) are in main.tsx to avoid
 * duplicate ClerkProvider. This component uses AuthProvider (local) for
 * journey instances and theme/sidebar providers.
 */

import { DEFAULT_THEME_BY_APP } from '@tonnie/design-tokens';
import { AccountPortalProvider } from '@tonnie/account';
import { LocaleProvider } from '@tonnie/locale';
import type { IAnalyticsClient, IErrorClient } from '@tonnie/observability';
import { ObservabilityProvider } from '@tonnie/observability';
import { AnalyticsProvider, LocaleBridge } from '@tonnie/sdk';
import { useEffect } from 'react';
import { Router } from 'wouter';

import { pruneLegacySessionKeys } from '@/features/platform/copilot/components/guide/guide-session';
import { CopilotCorrelationProvider } from '@/features/platform/copilot/lib/correlation-context';

import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import { InactivityGuard } from '@/components/shell/InactivityGuard';
import { ThemeLoader } from '@/components/shell/ThemeLoader';
import { ViewportGuard } from '@/components/shell/ViewportGuard';
import { InOutHandoffOnPhone } from '@/features/heroes/inout/components/InOutHandoffOnPhone';
import { agencyDossierInAddress } from '@/features/heroes/inout/lib/agency-dossier-address';
import { handoffInAddress } from '@/features/heroes/inout/lib/pending-handoff';
import { TraceIdBridge } from '@/core/components/TraceIdBridge';
import { ObservabilityAuthSync } from '@/core/components/ObservabilityAuthSync';
import { DeviceTimezoneSync } from '@/core/components/DeviceTimezoneSync';
import { AppToaster } from '@/core/components/AppToaster';
import { CoPilotEngineProvider } from '@/features/platform/copilot/context/CoPilotEngineProvider';
import { CITIZEN_ACCOUNT_PORTAL } from '@/features/platform/auth/account-portal';
import { EvidenceRetentionNoticeProvider } from '@/features/secondary/evidence-retention';
import { CheckUpCalculationReconciler } from '@/features/heroes/checkup/components/CheckUpCalculationReconciler';
import {
  AuthProvider,
  CoPilotSidebarProvider,
  CoPilotContextProvider,
  FeedbackSidebarProvider,
  JourneyInstanceProvider,
  ThemeProvider,
} from '../contexts';
import { AppRoutes } from '../routes';

// Minimum viewport width per platform guidelines (768px = md breakpoint)
const MIN_VIEWPORT_WIDTH = 768;

interface AppProps {
  analyticsClient: IAnalyticsClient;
  errorClient: IErrorClient;
}

export function App({ analyticsClient, errorClient }: AppProps) {
  // One-time cleanup of CoPilot guide-session keys created BEFORE the
  // 2026-04-26 journey-scoping change (radar 2026-04-28 — m-1). The
  // legacy entries cannot match a journey-scoped lookup, so they
  // accumulate without ever being read. Pruning on boot is cheap and
  // bounded — `pruneLegacySessionKeys` only touches localStorage keys
  // with the CoPilot prefix and no `::journey` suffix.
  useEffect(() => {
    pruneLegacySessionKeys();
  }, []);

  return (
    <ErrorBoundary errorClient={errorClient}>
      <LocaleProvider detectLanguage={true} debug={import.meta.env.DEV}>
        <ObservabilityProvider analytics={analyticsClient} errors={errorClient}>
          <TraceIdBridge />
          <LocaleBridge />
          {/*
            Citizen Web needs a wide screen; a phone is sent to the app. A
            calculation a portal's calculator handed over travels with it
            (D1-B) — inside the locale and the SDK, so that screen speaks the
            person's language and says the calculation back.
           */}
          <ViewportGuard
            minWidth={MIN_VIEWPORT_WIDTH}
            fallback={handoffInAddress() ? <InOutHandoffOnPhone /> : undefined}
            allowNarrow={agencyDossierInAddress()}
          >
            {/*
              CoPilot correlation provider — mounts ABOVE every CoPilot
              host page so `useCopilotCorrelation()` is available
              wherever a flow can stamp a fresh action id (start /
              restart / retake / discard / finalize). The provider's
              registered getter is read by the SDK interceptor wired
              in `main.tsx`, threading the id into `x-trace-id` on
              outbound requests. Radar 2026-04-28 — Logging Phase L-2.
             */}
            <CopilotCorrelationProvider debug={import.meta.env.DEV}>
              <AnalyticsProvider config={{ appName: 'journey-web', debug: import.meta.env.DEV }}>
                {/* Local AuthProvider extends SDK auth with journey instances */}
                <AuthProvider>
                  <ObservabilityAuthSync />
                  <DeviceTimezoneSync />
                  <InactivityGuard>
                    <JourneyInstanceProvider>
                      {/* L4 · F26 — the ONE observer of a running CheckUp calculation. */}
                      <CheckUpCalculationReconciler />
                      <ThemeProvider
                        appId="journey"
                        defaultTheme={DEFAULT_THEME_BY_APP.journey}
                        switchable
                        enableSystem
                        enableCustom
                      >
                        <ThemeLoader>
                          <CoPilotSidebarProvider>
                            <CoPilotContextProvider>
                              <CoPilotEngineProvider>
                                <FeedbackSidebarProvider>
                                  {/* Above the router so the account-deletion
                                      retention notice survives the sign-out
                                      redirect that unmounts Settings (W5.8). */}
                                  <EvidenceRetentionNoticeProvider>
                                    <AccountPortalProvider value={CITIZEN_ACCOUNT_PORTAL}>
                                      <Router>
                                        <AppRoutes />
                                      </Router>
                                    </AccountPortalProvider>
                                  </EvidenceRetentionNoticeProvider>
                                  <AppToaster />
                                </FeedbackSidebarProvider>
                              </CoPilotEngineProvider>
                            </CoPilotContextProvider>
                          </CoPilotSidebarProvider>
                        </ThemeLoader>
                      </ThemeProvider>
                    </JourneyInstanceProvider>
                  </InactivityGuard>
                </AuthProvider>
              </AnalyticsProvider>
            </CopilotCorrelationProvider>
          </ViewportGuard>
        </ObservabilityProvider>
      </LocaleProvider>
    </ErrorBoundary>
  );
}

export default App;
